<script lang="ts">
  import { onMount, tick } from "svelte";
  import { fade, fly, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { WorldRenderer, type WorldHud as WorldHudData } from "../world/WorldRenderer.ts";
  import { loadWorldData } from "../../data.ts";
  import { session, go } from "../stores/session.svelte.ts";
  import { settings } from "../stores/settings.svelte.ts";
  import { watchReplay } from "../stores/duel.svelte.ts";
  import { getSocket, presence, send, listen, logout as netLogout } from "../stores/net.svelte.ts";
  import { spawnFor, MAP_TITLES } from "../world/mapGraph.ts";
  import { findNpcByChar, type WorldNpc } from "../world/npcs.ts";
  import type { NpcBust } from "@ygoevo/yo2-data";
  import DataWindow from "../components/ui/DataWindow.svelte";
  import Toast from "../components/ui/Toast.svelte";
  import MapSwitch from "../components/MapSwitch.svelte";
  import NpcDialog from "../components/NpcDialog.svelte";
  import PlayerDialog from "../components/PlayerDialog.svelte";
  import TradeWindow from "../components/TradeWindow.svelte";
  import ShopDialog from "../components/ShopDialog.svelte";
  import BoxShop from "../components/BoxShop.svelte";
  import CharCreate from "./CharCreate.svelte";
  import StarterPicker from "../components/StarterPicker.svelte";
  import StreakReward from "../components/StreakReward.svelte";
  import BetaWelcome from "../components/BetaWelcome.svelte";
  import WorldLoader from "../components/WorldLoader.svelte";
  import QueueOverlay from "../components/QueueOverlay.svelte";
  import DeckChooser from "../components/DeckChooser.svelte";
  import RankBadge from "../components/RankBadge.svelte";
  import SettingsDialog from "../components/SettingsDialog.svelte";
  import WorldMenu from "../components/WorldMenu.svelte";
  import WorldHud from "../components/WorldHud.svelte";
  import VirtualJoystick from "../components/VirtualJoystick.svelte";
  import { isPhone } from "../device.svelte.ts";
  import { type ServerMessage, type RankMode, type ShopEra, type RankSchool } from "@ygoevo/world-protocol";
  import { chibiFrom } from "../../render/ChibiAvatar.ts";
  import { mm, resetMm } from "../stores/mm.svelte.ts";
  import { trade, resetTrade } from "../stores/trade.svelte.ts";
  import { createHotkey, onEscape } from "../hotkeys.ts";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  let host: HTMLDivElement;
  let hud = $state<WorldHudData>({ area: "", players: 1 });
  let renderer = $state<WorldRenderer | null>(null);
  let mapOpen = $state(false);
  let menuOpen = $state(false);
  let fashionOpen = $state(false);
  let settingsOpen = $state(false);

  // Boot "dive" loader, shown until the first map's terrain has decoded, driven
  // by real asset-load progress reported from the renderer.
  let loading = $state(true);
  let loadProgress = $state(0.03);
  let loadPhase = $state("Establishing link");
  const destArea = $derived(MAP_TITLES[session.mapId] ?? "The World");

  // Inter-map loading screen (door walk + map-disk fast-travel). Shown only when a
  // switch actually takes a beat, a short delay swallows instant (cached) warps so
  // the loader never flashes; a warm preloaded neighbour shows nothing.
  let warping = $state(false);
  let warpArea = $state("");
  let warpTimer: ReturnType<typeof setTimeout> | undefined;

  // Card shop (click the café in Station Square) + the first-join free pack offer.
  let shopOpen = $state(false);
  // Which physical shop is open, drives the era of packs the catalog returns. Set
  // when the player clicks a shop building (Kame Game in Domino vs Academy's shop).
  let activeShop = $state<ShopEra>("kame");
  let catalog = $state<Extract<ServerMessage, { t: "ShopCatalog" }> | null>(null);
  let starterDismissed = $state(false);
  const wallet = $derived(session.loginOk?.wallet ?? { dp: 0, shards: 0, packs: {} });
  // Starter picker waits until the beta welcome + daily reward (if any) are collected, so the
  // welcome modals don't stack on a brand-new player's first login.
  const showStarter = $derived(!!catalog?.starter.needed && !starterDismissed && !session.dailyReward && !session.betaWelcome);

  // The Beta Gold Duel Disk is revealed via the day-2 daily reward (StreakReward) now, not a
  // separate one-time popup, so there's no dedicated golden-award modal here anymore.

  // Overworld NPC conversation: opened by clicking a character in the world.
  let activeNpc = $state<WorldNpc | null>(null);
  let npcBusts = $state<NpcBust[]>([]);
  const activeBust = $derived(
    activeNpc ? (npcBusts.find((b) => b.id === activeNpc!.char) ?? null) : null,
  );

  // Pre-duel deck picker: every duel-entry action opens it, and the deck the player picks rides the
  // entry message to the server (there's no equipped deck). `run` fires once a deck name is chosen.
  let chooser = $state<{ subtitle: string; run: (deck: string) => void } | null>(null);
  function chooseDeck(subtitle: string, run: (deck: string) => void) {
    menuOpen = false;
    chooser = { subtitle, run };
  }

  function onNpcDuel(npc: WorldNpc) {
    const char = npc.char;
    const who = npc.name;
    activeNpc = null;
    chooseDeck(`Duel ${who}`, (deck) => send({ t: "DuelPractice", npc: char, deck }));
  }

  // ── another player's card (click a player) + duel invites ──
  let activePlayer = $state<Extract<ServerMessage, { t: "PlayerProfile" }>["profile"] | null>(null);
  let incomingInvite = $state<{ fromUserId: string; fromName: string } | null>(null);
  let inviteSent = $state<string | null>(null);
  let inviteSentTimer: ReturnType<typeof setTimeout> | undefined;

  function duelPlayer() {
    if (!activePlayer) return;
    const toUserId = activePlayer.userId;
    const who = activePlayer.name;
    activePlayer = null;
    chooseDeck(`Challenge ${who}`, (deck) => {
      send({ t: "DuelInvite", toUserId, deck });
      inviteSent = who;
      clearTimeout(inviteSentTimer);
      inviteSentTimer = setTimeout(() => (inviteSent = null), 5000);
    });
  }
  function tradePlayer() {
    if (!activePlayer) return;
    send({ t: "TradeRequest", toUserId: activePlayer.userId });
    activePlayer = null;
  }

  // ── "Go to" a player (teleport to them; THEY must accept first) ──
  let gotoIncoming = $state<{ fromUserId: string; fromName: string } | null>(null);
  let gotoSent = $state<string | null>(null);
  let gotoNotice = $state<string | null>(null);
  let gotoSentTimer: ReturnType<typeof setTimeout> | undefined;

  function goToPlayer() {
    if (!activePlayer) return;
    send({ t: "GoToRequest", toUserId: activePlayer.userId });
    gotoSent = activePlayer.name;
    clearTimeout(gotoSentTimer);
    gotoSentTimer = setTimeout(() => (gotoSent = null), 10000);
    activePlayer = null;
  }
  function acceptGoTo() {
    if (!gotoIncoming) return;
    send({ t: "GoToRespond", fromUserId: gotoIncoming.fromUserId, accept: true });
    gotoIncoming = null;
  }
  function declineGoTo() {
    if (!gotoIncoming) return;
    send({ t: "GoToRespond", fromUserId: gotoIncoming.fromUserId, accept: false });
    gotoIncoming = null;
  }
  function acceptInvite() {
    if (!incomingInvite) return;
    const fromUserId = incomingInvite.fromUserId;
    const who = incomingInvite.fromName;
    incomingInvite = null;
    chooseDeck(`Accept ${who}'s challenge`, (deck) => send({ t: "DuelAccept", fromUserId, deck }));
  }
  function declineInvite() {
    if (!incomingInvite) return;
    send({ t: "DuelDecline", fromUserId: incomingInvite.fromUserId });
    incomingInvite = null;
  }

  // ── trading ──
  let errorMsg = $state<string | null>(null);
  let errorTimer: ReturnType<typeof setTimeout> | undefined;
  function acceptTradeReq() {
    if (!trade.incoming) return;
    send({ t: "TradeRespond", fromUserId: trade.incoming.fromUserId, accept: true });
    trade.incoming = null;
  }
  function declineTradeReq() {
    if (!trade.incoming) return;
    send({ t: "TradeRespond", fromUserId: trade.incoming.fromUserId, accept: false });
    trade.incoming = null;
  }
  function cancelTrade() {
    send({ t: "TradeCancel" });
    resetTrade();
  }

  // Enter a matchmaking pool. AI practice now lives in the overworld (duel an NPC),
  // so the menu offers the two PvP queues. Pick a deck first, then queue with it (the search
  // overlay only appears once a deck is chosen, so cancelling the picker is a clean no-op).
  function startQueue(mode: RankMode) {
    chooseDeck(mode === "ranked" ? "Ranked Duel" : "Unranked Duel", (deck) => {
      mm.searching = true;
      mm.mode = mode;
      mm.format = "world";
      mm.waited = 0;
      mm.pool = 0;
      mm.found = false;
      send({ t: "QueueJoin", mode, deck });
    });
  }

  // In-world menu overlay: the few off-world actions (duel, deck, avatar, logout).
  const menuItems = [
    { icon: "trophy", label: "Ranked Duel", desc: "Rated PvP, affects your rank", action: () => startQueue("ranked") },
    { icon: "swords", label: "Unranked Duel", desc: "Casual PvP, no rating", action: () => startQueue("unranked") },
    { icon: "ranks", label: "Leaderboard", desc: "Top ranked duelists", action: () => go("leaderboard") },
    { icon: "deck", label: "Deck Editor", desc: "Build, craft & dismantle cards", action: () => go("deck") },
    { icon: "edit", label: "Customize Avatar", desc: "Change your duelist's look", action: () => { menuOpen = false; session.customizing = true; } },
    { icon: "replay", label: "Watch Replay", desc: "Play back a saved replay file", action: () => { menuOpen = false; void watchReplay("world"); } },
    { icon: "gear", label: "Settings", desc: "Audio, animations & preferences", action: () => { menuOpen = false; settingsOpen = true; } },
  ];

  function logout() {
    netLogout(); // revoke the token over the socket + drop to an anonymous connection
    go("title");
  }

  // ── Hotkeys (TanStack) ──────────────────────────────────────────────────────
  // Escape cascade, M to toggle the menu, and Enter to jump into chat. Single-key
  // shortcuts default to `ignoreInputs:true`, so M / Enter never fire while the
  // chat box is focused; Escape we mark `ignoreInputs:true` too (chat owns its own
  // Escape-to-blur), and `enabled:!showStarter` keeps the mandatory starter pick
  // from being bypassed.
  function escapeCascade() {
    if (chooser) chooser = null;
    else if (mm.searching && !mm.found) { resetMm(); send({ t: "QueueLeave" }); }
    else if (activePlayer) activePlayer = null;
    else if (incomingInvite) declineInvite();
    else if (activeNpc) activeNpc = null;
    else if (session.customizing) session.customizing = false;
    else if (shopOpen) shopOpen = false;
    else if (fashionOpen) fashionOpen = false;
    else if (settingsOpen) settingsOpen = false;
    else if (mapOpen) mapOpen = false;
    else menuOpen = !menuOpen;
  }

  // Enter focuses the chat box, gated only by modals/dialogs that themselves own Enter (NPC talk,
  // daily reward, the starter pick) and the full-screen overlays (menu/map/shop/settings/fashion/
  // customizer/player+trade dialogs), plus before the world has loaded. Transient toasts (errors,
  // trade notices) and passive "waiting" states (invite sent, matchmaking search) deliberately do
  // NOT block it, so you can always just press Enter to talk.
  const worldIdle = $derived(
    !loading && !menuOpen && !mapOpen && !shopOpen && !fashionOpen && !settingsOpen && !session.customizing &&
    !activeNpc && !activePlayer && !incomingInvite && !chooser &&
    !trade.incoming && !trade.open && !trade.result &&
    !session.dailyReward && !showStarter,
  );

  function focusChat() {
    if (isPhone()) {
      chatOpen = true;
      chatUnread = 0;
      void tick().then(() => chatInput?.focus());
      return;
    }
    chatInput?.focus();
  }

  onEscape(escapeCascade, () => ({ enabled: !showStarter, ignoreInputs: true }));
  createHotkey("M", () => { menuOpen = !menuOpen; }, () => ({ enabled: !showStarter }));
  createHotkey("Enter", focusChat, () => ({ enabled: worldIdle || trade.open, conflictBehavior: "allow" }));

  /** Fast-travel via the map disk; keeps session state in sync for remounts. */
  function warpTo(mapId: string): void {
    const spawn = spawnFor(mapId);
    session.mapId = mapId;
    session.spawn = spawn;
    renderer?.warpTo(mapId, spawn);
  }

  type ChatLine = { channel: string; from: string; text: string };
  let chat = $state<ChatLine[]>([]);
  let chatText = $state("");
  let chatFocused = $state(false);
  let chatInput = $state<HTMLInputElement | null>(null);
  let chatOpen = $state(false);
  let chatUnread = $state(0);
  // Chat channel: "world" = global (default, everyone everywhere), "map" = local (this map only).
  let chatChannel = $state<"world" | "map">("world");

  // Global online roster (count pill + the clickable player list). Pushed by the server on every
  // connect/disconnect/rename/map-change. mapId → area title so the list shows WHERE everyone is.
  const onlinePlayers = $derived(presence.players);
  const rosterForHud = $derived(
    onlinePlayers.map((p) => ({ userId: p.userId, name: p.name, area: p.mapId ? MAP_TITLES[p.mapId] ?? p.mapId : undefined })),
  );

  // Chat-channel tab styling. Active = solid neon-cyan fill + dark ink (max contrast, reads over any
  // map); inactive = near-opaque void backing + cyan-edged ring so it never washes out on a bright
  // scene. Both fully opaque on purpose — the old translucent cyan-on-cyan tab was unreadable.
  const chanBtn = (active: boolean): string =>
    "chamfer-btn px-3 py-1 font-display text-[0.66rem] font-semibold uppercase tracking-[0.16em] transition-colors " +
    (active
      ? "bg-cyan text-void glow-cyan"
      : "bg-void-2/90 text-ink-dim ring-1 ring-cyan-dim/30 hover:bg-void-2 hover:text-ink");

  const name = $derived(session.loginOk?.avatar.name ?? "Duelist");
  const dp = $derived(session.loginOk?.wallet.dp ?? 0);
  const look = $derived(chibiFrom(session.loginOk?.avatar));
  const myRank = $derived(session.loginOk?.ranking ?? null);

  // GX dorm colour that frames the avatar + tints the tier tag (cyan before placement).
  const SCHOOL_COLOR: Record<RankSchool, string> = {
    beginner: "#9fb0c3",
    slifer: "#ff5a4d",
    ra: "#ffc23c",
    obelisk: "#46b6ff",
    master: "#c98bff",
  };
  const fc = $derived(myRank ? (SCHOOL_COLOR[myRank.school] ?? SCHOOL_COLOR.beginner) : "var(--color-cyan)");

  listen("Chat", (m) => {
    chat.push({ channel: m.channel, from: m.from, text: m.text });
    if (chat.length > 100) chat.shift();
    if (isPhone() && !chatOpen) chatUnread += 1;
    // Float a bubble above the speaker's chibi for non-system lines.
    if (m.channel !== "system" && m.from !== "SERVER") renderer?.showSpeechBubble(m.from, m.text);
  });

  // Matchmaking heartbeat → the floating QueueOverlay. searching:false (left/cancelled)
  // resets it; a MatchFound flips to the "found" flourish before DuelStart switches screens.
  listen("QueueStatus", (m) => {
    if (!m.searching) return resetMm();
    mm.searching = true;
    mm.mode = m.mode;
    mm.format = m.format ?? "world";
    mm.waited = m.waited;
    mm.pool = m.pool;
  });
  listen("MatchFound", () => (mm.found = true));

  // Clicking another player fetches their card; an incoming duel challenge prompts to accept.
  listen("PlayerProfile", (m) => (activePlayer = m.profile));
  listen("DuelInviteIncoming", (m) => (incomingInvite = { fromUserId: m.fromUserId, fromName: m.fromName }));

  // "Go to": incoming ask → prompt; grant → warp to the granted spot (server-resolved).
  listen("GoToIncoming", (m) => (gotoIncoming = { fromUserId: m.fromUserId, fromName: m.fromName }));
  listen("GoToGrant", (m) => {
    gotoSent = null;
    const spawn = { x: m.x, y: m.y, facing: m.facing };
    session.mapId = m.mapId;
    session.spawn = spawn;
    void renderer?.warpTo(m.mapId, spawn, true); // force: they may be on OUR map already
  });
  listen("GoToClosed", (m) => {
    gotoSent = null;
    gotoNotice = m.reason === "declined" ? "They declined your Go to request." : "Go to failed, the player left.";
  });

  // Trading: invite → window → result/notice. The window itself drives offers via TradeState.
  listen("TradeRequestIncoming", (m) => (trade.incoming = { fromUserId: m.fromUserId, fromName: m.fromName }));
  listen("TradeStarted", (m) => { trade.incoming = null; trade.open = true; trade.withName = m.withName; trade.state = null; });
  listen("TradeState", (m) => (trade.state = m.state));
  listen("TradeClosed", (m) => {
    resetTrade();
    if (m.reason === "completed") {
      trade.result = { gained: m.gained ?? [], lost: m.lost ?? [] };
      send({ t: "CosmeticInventoryGet" }); // re-sync cosmetic ownership (a trade may have swapped some)
    } else trade.notice = m.reason === "declined" ? "Trade declined." : m.reason === "offline" ? "Trade ended, the player left." : "Trade cancelled.";
  });
  // Rebuild the owned-cosmetic lists the customizer reads from the authoritative inventory (after a
  // trade swaps items in/out). Always-owned defaults are unioned in so a glitchy row can't strip them.
  listen("CosmeticInventory", (m) => {
    if (!session.loginOk) return;
    const ids = (kind: string) => m.items.filter((i) => i.kind === kind).map((i) => i.itemId);
    session.loginOk.ownedSleeves = [...new Set(["default", ...ids("sleeve")])];
    session.loginOk.ownedDuelDisks = [...new Set(["dd_01_c01", ...ids("dueldisk")])];
    session.loginOk.ownedOutfits = [...new Set(["dc_01_c01", "dc_01_c02", "dc_01_c03", "dc_01_c04", ...ids("outfit")])];
  });
  listen("Error", (m) => { errorMsg = m.message; clearTimeout(errorTimer); errorTimer = setTimeout(() => (errorMsg = null), 3500); });

  // Shop catalog (today's packs + first-join offer); buying/opening/claiming all
  // return the authoritative wallet, keep the HUD DP + shop view in sync here.
  listen("ShopCatalog", (m) => (catalog = m));
  listen("ShopBought", (m) => { if (session.loginOk) session.loginOk.wallet = m.wallet; });
  listen("PackOpened", (m) => {
    if (session.loginOk) session.loginOk.wallet = m.wallet;
    // Opening packs adds cards → refresh the catalog so each pack's set-completion % is current.
    if (shopOpen) send({ t: "ShopList", shop: activeShop });
  });

  // A box was opened or a Shards-pick made: sync the wallet (DP spent on open / shards spent on
  // pick / shards gained from a dupe) and unlock the item so the customizer/wardrobe show it owned.
  // (BoxShop drives its own reveal animation.)
  const unlockCosmetic = (kind: string, itemId: string) => {
    if (!session.loginOk) return;
    const key = kind === "sleeve" ? "ownedSleeves" : kind === "dueldisk" ? "ownedDuelDisks" : kind === "outfit" ? "ownedOutfits" : null;
    if (key && !session.loginOk[key].includes(itemId)) session.loginOk[key] = [...session.loginOk[key], itemId];
  };
  listen("BoxResult", (m) => {
    if (!session.loginOk) return;
    session.loginOk.wallet = m.wallet;
    unlockCosmetic(m.kind, m.itemId);
  });
  listen("BoxPicked", (m) => {
    if (!session.loginOk) return;
    session.loginOk.wallet = m.wallet;
    unlockCosmetic(m.kind, m.itemId);
  });

  // Re-request the catalog every time the shop opens, so a stale/missed reply (e.g.
  // the one-shot mount request lost during a server restart) self-heals instead of
  // hanging on "Loading the shelves". The socket queues it if briefly disconnected.
  $effect(() => {
    if (shopOpen) send({ t: "ShopList", shop: activeShop });
  });

  // Freeze world movement while the mandatory first-join deck pick is up, so the
  // avatar can't be walked around behind the (near-opaque) overlay.
  $effect(() => {
    renderer?.setPaused(showStarter);
  });

  // The avatar customizer is a fullscreen opaque overlay — stop rendering the overworld entirely
  // while it's open (no reason to draw it at 60fps behind the panel). Resumes on close.
  $effect(() => {
    renderer?.setRendering(!session.customizing);
  });

  // Re-apply the overworld frame cap when the quality tier changes (manual or auto perf monitor).
  $effect(() => {
    settings.renderQuality;
    settings.autoTier;
    renderer?.applyQuality();
  });

  function sendChat() {
    const text = chatText.trim();
    if (text) send({ t: "Say", channel: chatChannel, text });
    chatText = "";
    chatInput?.blur(); // return to the world so WASD movement resumes
  }

  // Enter sends (and exits chat); Escape cancels. World's global Escape is
  // `ignoreInputs:true`, so it stays out of the way while the box is focused.
  function onChatKey(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      sendChat();
    } else if (e.key === "Escape") {
      e.preventDefault();
      chatText = "";
      chatInput?.blur();
    }
  }

  onMount(() => {
    let disposed = false;
    const t0 = performance.now();
    (async () => {
      loadPhase = "Establishing link";
      loadProgress = 0.03;
      const data = await loadWorldData();
      if (disposed || !session.loginOk) return;
      npcBusts = data.npcs.busts ?? [];
      loadProgress = 0.06;
      // Pull today's shop selection + the first-join free-pack offer (drives both
      // the shop overlay and the welcome picker).
      send({ t: "ShopList" });
      // Dev: `?npc=<char>` (with ?worldmock=1) opens that NPC's dialog on load, for
      // designing/screenshotting the conversation UI deterministically.
      const npcParam = new URLSearchParams(location.search).get("npc");
      if (npcParam) activeNpc = findNpcByChar(npcParam) ?? null;
      const r = new WorldRenderer({
        socket: getSocket(),
        data,
        loginOk: session.loginOk,
        onHud: (h) => (hud = h),
        onNpcClick: (npc) => (activeNpc = npc),
        onPlayerClick: (userId) => send({ t: "PlayerProfileGet", userId }),
        onInteract: (kind, id) => {
          if (kind !== "shop") return;
          // Domino's storefront = the Kame Game shop (original era); the academy's = GX era.
          activeShop = id === "shop_academy" ? "academy" : "kame";
          shopOpen = true;
        },
        onProgress: ({ value, phase }) => {
          loadProgress = value;
          loadPhase = phase;
        },
        onTransition: ({ active, area }) => {
          if (active) {
            warpArea = area;
            loadProgress = 0.05;
            loadPhase = "Loading area";
            clearTimeout(warpTimer);
            // 140ms grace: a cached warp resolves first and never trips the loader.
            warpTimer = setTimeout(() => (warping = true), 140);
          } else {
            clearTimeout(warpTimer);
            warping = false;
          }
        },
      });
      renderer = r;
      await r.mount(host, session.mapId, session.spawn ?? spawnFor(session.mapId));
      if (disposed) return;
      loadProgress = 1;
      // Give the ring a short beat to visibly fill to 100% before the reveal (the
      // dive "landing"), while still honouring a minimum on-screen time so a fast
      // cached load doesn't flash the dive screen in and straight back out.
      const elapsed = performance.now() - t0;
      await new Promise((res) => setTimeout(res, Math.max(700 - elapsed, 340)));
      if (!disposed) loading = false;
    })();
    return () => {
      disposed = true;
      clearTimeout(warpTimer);
      // Stash exactly where we stood so returning to the world (from a duel, deck editor,
      // leaderboard) drops us back here, starting a duel no longer teleports us to the spawn.
      if (renderer) session.spawn = renderer.currentSpawn();
      renderer?.destroy();
      renderer = null;
    };
  });
</script>

{#snippet ic(n: string, cls: string)}
  <svg
    viewBox="0 0 24 24"
    class={cls}
    fill="none"
    stroke="currentColor"
    stroke-width="1.7"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    {#if n === "swords"}
      <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
      <line x1="13" x2="19" y1="19" y2="13" />
      <line x1="16" x2="20" y1="16" y2="20" />
      <line x1="19" x2="21" y1="21" y2="19" />
      <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
      <line x1="5" x2="9" y1="14" y2="18" />
      <line x1="7" x2="4" y1="17" y2="20" />
      <line x1="3" x2="5" y1="19" y2="21" />
    {:else if n === "trophy"}
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    {:else if n === "ranks"}
      <line x1="4" x2="4" y1="21" y2="11" />
      <line x1="12" x2="12" y1="21" y2="4" />
      <line x1="20" x2="20" y1="21" y2="14" />
      <line x1="2.5" x2="21.5" y1="21" y2="21" />
    {:else if n === "deck"}
      <path d="M12.8 2.2a2 2 0 0 0-1.6 0L2.6 6.1a1 1 0 0 0 0 1.8l8.6 3.9a2 2 0 0 0 1.6 0l8.6-3.9a1 1 0 0 0 0-1.8Z" />
      <path d="m22 17.7-9.2 4.1a2 2 0 0 1-1.6 0L2 17.7" />
      <path d="m22 12.7-9.2 4.1a2 2 0 0 1-1.6 0L2 12.7" />
    {:else if n === "edit"}
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    {:else if n === "arrow"}
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    {:else if n === "close"}
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    {:else if n === "logout"}
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    {:else if n === "gear"}
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    {/if}
  </svg>
{/snippet}

<!-- Pixi world canvas fills the full viewport; the HUD floats over it.
     The overlay is click-through except its interactive clusters, so the world
     stays fully walkable underneath. -->
<div bind:this={host} class="fixed inset-0 bg-void"></div>

<!-- persistent overworld HUD overlay (shared WorldHud component — also rendered by the Texture Pack editor) -->
<WorldHud
  {name}
  {look}
  rankLabel={myRank?.label ?? ""}
  accent={fc}
  {dp}
  shards={wallet.shards}
  area={hud.area}
  online={presence.synced ? onlinePlayers.length : null}
  players={rosterForHud}
  selfId={session.loginOk?.userId ?? ""}
  onMap={() => (mapOpen = true)}
  onShop={() => (fashionOpen = true)}
  onMenu={() => (menuOpen = true)}
  onPlayer={(userId: string) => send({ t: "PlayerProfileGet", userId })}
/>

{#if isPhone()}
  <VirtualJoystick onmove={(x, y) => renderer?.setStick(x, y)} />
{/if}

<!-- Chat stays ambient on desktop. On landscape phones it collapses to an unread-aware
     thumb button and opens as a compact tray, leaving the world unobstructed by default. -->
<div
  class:hidden={isPhone() && !chatOpen}
  class="phone-chat-tray pointer-events-none fixed bottom-3 flex w-[360px] max-w-[78vw] flex-col gap-1.5 {trade.open ? 'z-[60]' : 'z-30'}"
>
  {#if isPhone()}
    <div class="pointer-events-auto flex min-h-11 items-center justify-between border-b border-cyan/20 px-1 pb-1.5">
      <span class="font-display text-xs font-bold uppercase tracking-[0.16em] text-cyan">Chat</span>
      <button
        onclick={() => { chatOpen = false; chatInput?.blur(); }}
        class="grid size-11 place-items-center chamfer-btn text-ink-dim transition-colors active:bg-cyan/15 active:text-cyan"
        style="--c:7px"
        aria-label="Close chat"
      >
        <svg viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
    </div>
  {/if}

  <div
    class="chat-log flex max-h-[168px] flex-col justify-end gap-0.5 overflow-hidden px-2 py-1 text-sm leading-snug transition-colors duration-200 {chatFocused
      ? 'chamfer bg-void/55'
      : ''}"
    style="--c:10px"
  >
    {#each chat.slice(-8) as line, i (i)}
      <div class="ch-{line.channel} drop-shadow-[0_1px_2px_rgba(0,0,0,0.95)]">
        <span class="text-cyan/80">&lt;{line.from}&gt;</span>
        <span class="text-ink">{line.text}</span>
      </div>
    {/each}
  </div>

  <!-- Channel selector: World (global, default) or Local (this map). -->
  <div class="pointer-events-auto flex gap-1 self-start">
    <button onclick={() => (chatChannel = "world")} class={chanBtn(chatChannel === "world")} style="--c:6px">
      World
    </button>
    <button onclick={() => (chatChannel = "map")} class={chanBtn(chatChannel === "map")} style="--c:6px">
      Local
    </button>
  </div>

  <input
    bind:this={chatInput}
    bind:value={chatText}
    onkeydown={onChatKey}
    onfocus={() => { chatFocused = true; chatUnread = 0; }}
    onblur={() => (chatFocused = false)}
    placeholder={chatChannel === "world" ? "Press Enter — message everyone" : "Press Enter — message this area"}
    class="chamfer-btn pointer-events-auto w-full bg-void-2/75 px-3 py-1.5 font-body text-sm text-ink outline-none ring-1 ring-cyan/20 transition placeholder:text-ink-dim/70 focus:ring-cyan/70"
    style="--c:8px"
  />
</div>

{#if isPhone() && !chatOpen}
  <button
    onclick={focusChat}
    class="phone-chat-launcher pointer-events-auto fixed grid size-12 place-items-center chamfer-btn border border-cyan/45 bg-void/90 text-cyan shadow-[0_8px_24px_rgba(0,0,0,0.45)] transition-colors active:bg-cyan/15 {trade.open ? 'z-[60]' : 'z-30'}"
    style="--c:9px"
    aria-label={chatUnread > 0 ? `Open chat, ${chatUnread} unread` : "Open chat"}
  >
    <svg viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3v-3a4 4 0 0 1-1-2.65V7a4 4 0 0 1 4-4h11a4 4 0 0 1 4 4Z" />
      <path d="M7 9h10M7 13h7" />
    </svg>
    {#if chatUnread > 0}
      <span class="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-magenta px-1 font-display text-[0.62rem] font-bold leading-5 text-void">
        {chatUnread > 99 ? "99+" : chatUnread}
      </span>
    {/if}
  </button>
{/if}

<MapSwitch open={mapOpen} current={session.mapId} onWarp={warpTo} onClose={() => (mapOpen = false)} />

<!-- ── NPC conversation ───────────────────────────────────────────────────── -->
{#if activeNpc}
  <NpcDialog
    npc={activeNpc}
    bust={activeBust}
    onClose={() => (activeNpc = null)}
    onDuel={onNpcDuel}
  />
{/if}

<!-- ── Card shop ──────────────────────────────────────────────────────────── -->
{#if shopOpen}
  <ShopDialog shop={activeShop} {catalog} {wallet} onClose={() => (shopOpen = false)} />
{/if}

<!-- ── Cosmetic box shop (outfits / disks / sleeves) ──────────────────────── -->
{#if fashionOpen}
  <BoxShop onClose={() => (fashionOpen = false)} />
{/if}

<!-- ── Settings (in-world overlay; shares SettingsPanel with the Free Duel route) ── -->
{#if settingsOpen}
  <SettingsDialog onClose={() => (settingsOpen = false)} />
{/if}

<!-- ── Avatar customizer (overlay over the world, blurred backdrop) ────────── -->
{#if session.customizing}
  <CharCreate onClose={() => (session.customizing = false)} />
{/if}


<!-- ── Pre-duel deck picker (every duel-entry action chooses a deck first) ──── -->
{#if chooser}
  <DeckChooser
    format="world"
    subtitle={chooser.subtitle}
    onpick={(deck) => { const run = chooser!.run; chooser = null; run(deck); }}
    oncancel={() => (chooser = null)}
    onbuild={() => { chooser = null; go("deck"); }}
  />
{/if}

<!-- ── Matchmaking search (floating, non-blocking) ────────────────────────── -->
<QueueOverlay />

<!-- ── Another player's card (click a player in the world) ─────────────────── -->
{#if activePlayer}
  <PlayerDialog
    profile={activePlayer}
    onClose={() => (activePlayer = null)}
    onDuel={duelPlayer}
    onTrade={tradePlayer}
    onGoTo={goToPlayer}
  />
{/if}

<!-- ── Incoming "Go to" request ────────────────────────────────────────────── -->
{#if gotoIncoming}
  <div class="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center" transition:fly={{ y: -30, duration: 220, easing: expoOut }}>
    <div class="pointer-events-auto">
      <DataWindow c={14}>
        <div class="flex items-center gap-4 px-5 py-3">
          <div class="leading-tight">
            <div class="font-display text-sm font-bold tracking-wide text-lime">Go to Request</div>
            <div class="text-xs text-ink-dim"><span class="font-semibold text-ink">{gotoIncoming.fromName}</span> wants to come to you</div>
          </div>
          <button onclick={acceptGoTo} class="chamfer-btn bg-lime/15 px-4 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-lime transition-colors hover:bg-lime/25" style="--c:7px">Accept</button>
          <button onclick={declineGoTo} class="chamfer-btn bg-void-2/60 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-ink-dim transition-colors hover:text-magenta" style="--c:7px">Decline</button>
        </div>
      </DataWindow>
    </div>
  </div>
{/if}

<!-- ── Go to request sent / declined notice ───────────────────────────────── -->
{#if gotoSent}
  <div class="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center" transition:fade>
    <div class="chamfer-btn bg-surface/90 px-5 py-2 font-display text-xs tracking-wide text-cyan" style="--c:8px">
      Go to request sent to <span class="font-bold">{gotoSent}</span>, waiting for them to accept…
    </div>
  </div>
{/if}
{#if gotoNotice}
  <button class="pointer-events-auto fixed inset-x-0 top-20 z-40 mx-auto block w-fit chamfer-btn bg-surface/90 px-5 py-2 font-display text-xs tracking-wide text-ink-dim" style="--c:8px" transition:fade onclick={() => (gotoNotice = null)}>
    {gotoNotice}
  </button>
{/if}

<!-- ── Incoming duel challenge ─────────────────────────────────────────────── -->
{#if incomingInvite}
  <div class="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center" transition:fly={{ y: -30, duration: 220, easing: expoOut }}>
    <div class="pointer-events-auto">
      <DataWindow c={14}>
        <div class="flex items-center gap-4 px-5 py-3">
          <div class="leading-tight">
            <div class="font-display text-sm font-bold tracking-wide text-magenta">Duel Challenge</div>
            <div class="text-xs text-ink-dim"><span class="font-semibold text-ink">{incomingInvite.fromName}</span> wants to duel you</div>
          </div>
          <button onclick={acceptInvite} class="chamfer-btn bg-lime/15 px-4 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-lime transition-colors hover:bg-lime/25" style="--c:7px">Accept</button>
          <button onclick={declineInvite} class="chamfer-btn bg-void-2/60 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-ink-dim transition-colors hover:text-magenta" style="--c:7px">Decline</button>
        </div>
      </DataWindow>
    </div>
  </div>
{/if}

<!-- ── Duel invite sent ────────────────────────────────────────────────────── -->
{#if inviteSent}
  <div class="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center" transition:fade>
    <div class="chamfer-btn bg-surface/90 px-5 py-2 font-display text-xs tracking-wide text-cyan" style="--c:8px">
      Challenge sent to <span class="font-bold">{inviteSent}</span>, waiting for them to accept…
    </div>
  </div>
{/if}

<!-- ── Incoming trade request ──────────────────────────────────────────────── -->
{#if trade.incoming}
  <div class="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center" transition:fly={{ y: -30, duration: 220, easing: expoOut }}>
    <div class="pointer-events-auto">
      <DataWindow c={14}>
        <div class="flex items-center gap-4 px-5 py-3">
          <div class="leading-tight">
            <div class="font-display text-sm font-bold tracking-wide text-cyan">Trade Request</div>
            <div class="text-xs text-ink-dim"><span class="font-semibold text-ink">{trade.incoming.fromName}</span> wants to trade cards</div>
          </div>
          <button onclick={acceptTradeReq} class="chamfer-btn bg-lime/15 px-4 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-lime transition-colors hover:bg-lime/25" style="--c:7px">Accept</button>
          <button onclick={declineTradeReq} class="chamfer-btn bg-void-2/60 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-ink-dim transition-colors hover:text-magenta" style="--c:7px">Decline</button>
        </div>
      </DataWindow>
    </div>
  </div>
{/if}

<!-- ── Trade window ────────────────────────────────────────────────────────── -->
{#if trade.open}
  <TradeWindow onClose={cancelTrade} />
{/if}

<!-- ── Trade completed ─────────────────────────────────────────────────────── -->
{#if trade.result}
  <div class="fixed inset-0 z-50 grid place-items-center" transition:fade>
    <button class="modal-scrim absolute inset-0 bg-void/50" onclick={() => (trade.result = null)} aria-label="Close"></button>
    <div use:draggableDialog role="dialog" aria-modal="true" aria-label="Trade complete" class="relative" transition:scale={{ start: 0.9, opacity: 0, duration: 240, easing: expoOut }}>
      <DataWindow c={16}>
        <div class="flex flex-col items-center gap-3 px-8 py-6">
          <div class="dialog-drag-handle -mb-2 -mt-3 flex w-full flex-col items-center pt-3" data-dialog-drag-handle>
            <div class="font-display text-2xl font-bold uppercase tracking-[0.1em] text-cyan text-glow">Trade Complete</div>
          </div>
          <div class="font-body text-sm text-ink-dim">
            Received <span class="font-bold text-lime">{trade.result.gained.length}</span> · gave
            <span class="font-bold text-magenta">{trade.result.lost.length}</span>
          </div>
          <button onclick={() => (trade.result = null)} class="chamfer-btn mt-1 border border-cyan/60 bg-void/70 px-7 py-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-cyan transition-colors hover:bg-cyan/15" style="--c:8px">Done</button>
        </div>
      </DataWindow>
    </div>
  </div>
{/if}

<!-- ── Transient notices / errors ──────────────────────────────────────────── -->
{#if trade.notice}
  <button class="pointer-events-auto fixed inset-x-0 top-20 z-40 mx-auto block w-fit chamfer-btn bg-surface/90 px-5 py-2 font-display text-xs tracking-wide text-ink-dim" style="--c:8px" transition:fade onclick={() => (trade.notice = null)}>
    {trade.notice}
  </button>
{/if}
{#if errorMsg}
  <div class="pointer-events-none fixed inset-x-0 top-32 z-40 flex justify-center" transition:fade>
    <Toast kind="error">{errorMsg}</Toast>
  </div>
{/if}

<!-- ── Beta welcome grant (shown ahead of the daily reward / starter picker) ─── -->
{#if session.betaWelcome}
  <BetaWelcome dp={session.betaWelcome.dp} shards={session.betaWelcome.shards} onClose={() => (session.betaWelcome = null)} />
{/if}

<!-- ── Daily login-streak reward ──────────────────────────────────────────── -->
{#if session.dailyReward && !session.betaWelcome}
  <StreakReward
    streak={session.dailyReward.streak}
    dp={session.dailyReward.dp}
    cosmetic={session.dailyReward.cosmetic}
    gender={look.gender}
    onClose={() => (session.dailyReward = null)}
  />
{/if}

<!-- ── First-join free pack ───────────────────────────────────────────────── -->
{#if showStarter && catalog}
  <StarterPicker choices={catalog.starter.choices} onDone={() => (starterDismissed = true)} />
{/if}

<!-- ── In-world menu overlay (shared WorldMenu component — also rendered by the Texture Pack editor) ── -->
{#if menuOpen}
  <WorldMenu items={menuItems} {name} {dp} {look} rank={myRank} onClose={() => (menuOpen = false)} onLogout={logout} />
{/if}

<!-- ── Loaders: boot "dive" (first map) + inter-map switch (door / fast-travel) ─ -->
{#if loading}
  <WorldLoader progress={loadProgress} phase={loadPhase} area={destArea} />
{:else if warping}
  <WorldLoader progress={loadProgress} phase={loadPhase} area={warpArea} />
{/if}

<style>
  .phone-chat-tray {
    left: var(--world-ui-gutter);
    transform: scale(var(--world-ui-scale));
    transform-origin: bottom left;
  }

  /* Older chat lines fade out at the top so the log floats over the world. */
  .chat-log {
    -webkit-mask-image: linear-gradient(to top, #000 62%, transparent);
    mask-image: linear-gradient(to top, #000 62%, transparent);
  }

  :global(.phone) .phone-chat-tray {
    right: max(0.75rem, env(safe-area-inset-right));
    bottom: max(0.75rem, env(safe-area-inset-bottom));
    left: auto;
    width: min(360px, 52vw);
    max-width: none;
    gap: 0.5rem;
    padding: 0.5rem;
    overflow: hidden;
    background: color-mix(in oklab, var(--color-void) 96%, transparent);
    border: 1px solid color-mix(in oklab, var(--color-cyan) 35%, transparent);
    clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
    box-shadow: 0 12px 38px rgb(0 0 0 / 0.55);
  }
  :global(.phone) .phone-chat-tray .chat-log {
    min-height: 5.5rem;
    max-height: min(31vh, 9rem);
    padding-inline: 0.35rem;
  }
  :global(.phone) .phone-chat-tray input {
    min-height: 44px;
    font-size: 1rem;
  }
  :global(.phone) .phone-chat-tray button {
    min-height: 44px;
  }
  .phone-chat-launcher {
    right: max(0.75rem, env(safe-area-inset-right));
    bottom: max(0.75rem, env(safe-area-inset-bottom));
  }
</style>
